import React, { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import HomePage from "@/pages/HomePage";
import ViewCodePage from "@/pages/ViewCodePage";
import PoolResultsPage from "@/pages/PoolResultsPage";
import PoolDrawsPage from "@/pages/PoolDrawsPage";
import PoolFixturesPage from "@/pages/PoolFixturesPage";
import FreeGamesPage from "@/pages/FreeGamesPage";
import PostPage from "@/pages/PostPage";
import LoginPage from "@/pages/LoginPage";
import SupportPage from "@/pages/SupportPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import CookiesPage from "@/pages/CookiesPage";
import NotFoundPage from "@/pages/NotFoundPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ReferralPage from "@/pages/ReferralPage";
import AdminDashboard from "@/pages/AdminDashboard";
import WhatsAppButton from "@/components/LiveChat";
import CongratsPopup from "@/components/CongratsPopup";
import TopBar from "@/components/TopBar";
import { WHATSAPP_SALES_URL } from "@/lib/sales";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Attach auth token to all requests automatically
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("fixedraws_token");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthContext = React.createContext(null);
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-green-600"></div></div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin123okay/login" state={{ from: location }} replace />;
  return children;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem("fixedraws_token");
      const config = { withCredentials: true };
      if (token) config.headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/auth/me`, config);
      setUser(res.data);
    } catch {
      localStorage.removeItem("fixedraws_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    if (res.data.token) {
      localStorage.setItem("fixedraws_token", res.data.token);
    }
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    const token = localStorage.getItem("fixedraws_token");
    const config = { withCredentials: true };
    if (token) config.headers = { Authorization: `Bearer ${token}` };
    try { await axios.post(`${API}/auth/logout`, {}, config); } catch {}
    localStorage.removeItem("fixedraws_token");
    setUser(null);
  };

  useEffect(() => { checkAuth(); }, [checkAuth]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout }}>
      {!loading ? children : (
        <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-green-600"></div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

function WhatsAppRedirect() {
  useEffect(() => {
    window.location.replace(WHATSAPP_SALES_URL);
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-green-600 mx-auto mb-4"></div>
        <p className="text-gray-600 text-sm">Redirecting you to Madam Precious on WhatsApp…</p>
      </div>
    </div>
  );
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/subscribe" element={<WhatsAppRedirect />} />
      <Route path="/subscribe/:planId" element={<WhatsAppRedirect />} />
      <Route path="/subscription/view" element={<WhatsAppRedirect />} />
      <Route path="/buy" element={<WhatsAppRedirect />} />
      <Route path="/view-code" element={<ViewCodePage />} />
      <Route path="/pool/results" element={<PoolResultsPage />} />
      <Route path="/pool/draws" element={<PoolDrawsPage />} />
      <Route path="/pool/fixtures" element={<PoolFixturesPage />} />
      <Route path="/free-games" element={<FreeGamesPage />} />
      <Route path="/post/:postId" element={<PostPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/referral" element={<ReferralPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/cookies" element={<CookiesPage />} />
      <Route path="/admin123okay/login" element={<LoginPage />} />
      <Route path="/admin123okay/reset-password" element={<ResetPasswordPage />} />
      <Route path="/admin123okay/*" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    axios.post(`${API}/seed`).catch(() => {});
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <TopBar />
        <AppRouter />
        <WhatsAppButton />
        <CongratsPopup />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
